<template>
  <UIButton
    ref="buttonRef"
    :size
    :variant
    :fill
    :disabled
  >
    <template
      v-if="count"
      #trailing
    >
      <UIDropdownConfirm
        placement="bottom-end"
        :message="confirmMessage"
        @confirm="handleClear"
      >
        <template #default="{ toggle }">
          <div
            v-tooltip.top="disabled ? undefined : tooltipText"
            class="ui-button-count"
            :class="{ 'ui-button-count--disabled': disabled }"
            role="button"
            tabindex="0"
            :aria-label="tooltipText || 'Clear'"
            @click.stop="toggle"
            @keydown.enter.stop.prevent="toggle"
            @keydown.space.stop.prevent="toggle"
          >
            <span>{{ count }}</span>
            <XMarkIcon />
          </div>
        </template>
      </UIDropdownConfirm>
    </template>
  </UIButton>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'

import { ButtonFill, ButtonSize, ButtonVariant } from '@types'

import UIButton from './Button.vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import UIDropdownConfirm from '../Dropdown/DropdownConfirm.vue'

type Props = {
  count?: number

  size?: ButtonSize
  variant?: ButtonVariant
  fill?: ButtonFill

  disabled?: boolean

  tooltipText?: string
  confirmMessage?: string
}

type Emits = {
  'click:clear': []
}

const {
  count,
  size = 'default',
  variant = 'primary',
  fill = 'filled',
} = defineProps<Props>()

const emit = defineEmits<Emits>()

const buttonRef = useTemplateRef('buttonRef')

defineExpose({
  focus() {
    buttonRef.value?.focus()
  },
})

const handleClear = () => {
  emit('click:clear')
}
</script>

<style>
.ui-button-count {
  @apply w-[1.15rem] h-[1.15rem];
  @apply flex items-center justify-center;
  @apply shrink-0;
  @apply -ml-0.5 -my-1;
  @apply rounded-full;
  @apply text-[10px] font-bold;

  @apply text-(--button-count-text);
  @apply bg-(--button-count-bg);

  svg {
    @apply w-3;
    @apply hidden;
  }

  &:not(.ui-button-count--disabled):hover svg,
  &:not(.ui-button-count--disabled):focus svg {
    @apply inline;
  }

  &:not(.ui-button-count--disabled):hover span,
  &:not(.ui-button-count--disabled):focus span {
    @apply hidden;
  }

  /* size/variant/fill classes can be added when needed. */
  /* For now only default styles are used in the app. */
}
</style>
